import { darken } from 'polished';
import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import { bodyMedium, bodySmall } from '../paragraph';

export const resetButton = css`
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  color: inherit;
  background: none;
  border: none;
  margin: 0;
  padding: 0;
  cursor: pointer;
`;

export const IconWrapper = styled.span<{
  position: 'right' | 'left';
  noMargin?: boolean;
}>`
  width: 1.43em;
  height: 1.43em;
  margin-right: ${({ theme, position, noMargin = false }): string | number =>
    position === 'left' && noMargin === false ? theme.spacing_unit_x1 : 0};
  margin-left: ${({ theme, position, noMargin = false }): string | number =>
    position === 'right' && noMargin === false ? theme.spacing_unit_x1 : 0};
  order: ${({ position }): number => (position === 'right' ? 2 : 1)};
  display: flex;
  align-items: center;
  justify-content: center;

  & > svg {
    width: 100%;
  }
`;

export const button = css<{
  variant: 'primary' | 'secondary' | 'tertiary' | 'success' | 'alert';
  size: 'large' | 'medium' | 'small';
  disabled: boolean;
  fullWidth?: boolean;
}>`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  border-width: 1px;
  border-style: solid;
  transition: all 250ms ease;
  font-weight: 600;
  white-space: nowrap;
  cursor: ${({ disabled }): string =>
    disabled === true ? 'not-allowed' : 'pointer'};

  ${({ theme, fullWidth }): FlattenSimpleInterpolation => {
    return fullWidth
      ? css`
          @media (max-width: ${theme.breakpoints_sm}) {
            width: 100%;
          }
        `
      : css``;
  }}

  ${({ size }): FlattenSimpleInterpolation | undefined => {
    if (size === 'large') {
      return css`
        ${bodyMedium};
        height: 44px;
        padding: 0 24px;
        border-radius: 10px;
      `;
    }

    if (size === 'medium') {
      return css`
        ${bodySmall};
        height: 36px;
        padding: 0 20px;
        border-radius: 8px;
      `;
    }

    if (size === 'small') {
      return css`
        ${bodySmall};
        height: 28px;
        padding: 0 12px;
        border-radius: 6px;
      `;
    }

    return;
  }}

  ${({ theme, variant, disabled }): FlattenSimpleInterpolation | undefined => {
    if (variant === 'primary' && disabled === false) {
      return css`
        background-color: ${theme.palette_primary_100};
        color: ${theme.palette_white};
        border-color: ${theme.palette_primary_100};

        &:hover {
          background-color: ${theme.palette_primary_125};
          border-color: ${theme.palette_primary_125};
        }
      `;
    }

    if (variant === 'primary' && disabled === true) {
      return css`
        background-color: ${theme.palette_gray_5};
        color: ${theme.palette_gray_60};
        border-color: ${theme.palette_gray_5};
      `;
    }

    if (variant === 'secondary' && disabled === false) {
      return css`
        background-color: transparent;
        color: ${theme.palette_primary_100};
        border-color: ${theme.palette_primary_100};

        &:hover {
          background-color: ${theme.palette_primary_0};
          color: ${theme.palette_primary_100};
          border-color: ${theme.palette_primary_100};
        }
      `;
    }

    if (variant === 'secondary' && disabled === true) {
      return css`
        background-color: transparent;
        color: ${theme.palette_gray_60};
        border-color: ${theme.palette_gray_40};
      `;
    }

    if (variant === 'tertiary' && disabled === false) {
      return css`
        background-color: transparent;
        color: ${theme.palette_primary_100};
        border-color: transparent;
      `;
    }

    if (variant === 'tertiary' && disabled === true) {
      return css`
        background-color: transparent;
        color: ${theme.palette_gray_60};
        border-color: transparent;
      `;
    }

    if (variant === 'success' && disabled === false) {
      return css`
        background-color: ${theme.palette_success_5};
        color: ${theme.palette_success_100};
        border-color: ${theme.palette_success_5};

        &:hover {
          background-color: ${darken(0.05, theme.palette_success_5)};
          border-color: ${darken(0.05, theme.palette_success_5)};
        }
      `;
    }

    if (variant === 'success' && disabled === true) {
      return css`
        background-color: ${theme.palette_gray_5};
        color: ${theme.palette_gray_60};
        border-color: ${theme.palette_gray_5};
      `;
    }

    if (variant === 'alert' && disabled === false) {
      return css`
        background-color: ${theme.palette_alert_5};
        color: ${theme.palette_alert_100};
        border-color: ${theme.palette_alert_5};

        &:hover {
          background-color: ${darken(0.05, theme.palette_alert_5)};
          border-color: ${darken(0.05, theme.palette_alert_5)};
        }
      `;
    }

    if (variant === 'alert' && disabled === true) {
      return css`
        background-color: ${theme.palette_gray_5};
        color: ${theme.palette_gray_60};
        border-color: ${theme.palette_gray_5};
      `;
    }

    return;
  }}
`;

export const StyledButton = styled.button<{
  variant: 'primary' | 'secondary' | 'tertiary' | 'success' | 'alert';
  size: 'large' | 'medium' | 'small';
  disabled: boolean;
  fullWidth?: boolean;
}>`
  ${resetButton}
  ${button}
`;
